import type { LucideIcon } from 'lucide-react'
import {
  ArrowRight,
  Bell,
  Building2,
  CreditCard,
  Landmark,
  Receipt,
  UserRound,
  Wallet,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type FlowStage = {
  id: string
  step: number
  title: string
  summary: string
  records: string[]
  icon: LucideIcon
  tone: string
  iconTone: string
  highlight?: boolean
}

const FLOW_STAGES: FlowStage[] = [
  {
    id: 'customer-order',
    step: 1,
    title: 'Customer → order co.',
    summary: 'Prepaid & online on the booking.',
    records: ['PaymentTransaction'],
    icon: CreditCard,
    tone: 'border-sky-200 bg-sky-50 dark:border-sky-900/50 dark:bg-sky-950/30',
    iconTone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200',
  },
  {
    id: 'customer-job',
    step: 2,
    title: 'Customer → job co.',
    summary: 'COD at job / trip completion.',
    records: ['WorkerCODCollection'],
    icon: UserRound,
    tone: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30',
    iconTone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200',
  },
  {
    id: 'ledger',
    step: 3,
    title: 'Ledger accrual',
    summary: 'Per job when order co. ≠ job co.',
    records: ['ServiceInstanceLedgerEntry'],
    icon: Building2,
    tone: 'border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30',
    iconTone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-200',
  },
  {
    id: 'invoice',
    step: 4,
    title: 'Period invoice',
    summary: 'Roll up open ledger entries.',
    records: ['ServiceInstanceSettlementInvoice', 'Generate period'],
    icon: Landmark,
    tone: 'border-amber-200 bg-amber-50 ring-2 ring-amber-300/60 dark:border-amber-900/50 dark:bg-amber-950/30 dark:ring-amber-700/40',
    iconTone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100',
    highlight: true,
  },
  {
    id: 'payment',
    step: 5,
    title: 'Lump-sum payment',
    summary: 'Record inter-company settlement.',
    records: ['ServiceInstancePayment', 'Record payment'],
    icon: Receipt,
    tone: 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30',
    iconTone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200',
    highlight: true,
  },
  {
    id: 'notify',
    step: 6,
    title: 'Notifications',
    summary: 'Both companies alert their contacts.',
    records: ['Notification contacts', 'Delivery log'],
    icon: Bell,
    tone: 'border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/30',
    iconTone: 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-200',
  },
]

const PARALLEL_STAGES: FlowStage[] = [
  {
    id: 'worker-pay',
    step: 7,
    title: 'Worker → job co.',
    summary: 'Separate from inter-company flow.',
    records: ['WorkerPaymentInvoice', 'Payments to Company'],
    icon: Wallet,
    tone: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40',
    iconTone: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  },
]

function StageArrow() {
  return (
    <div className="flex shrink-0 items-center justify-center px-1 sm:px-2" aria-hidden>
      <ArrowRight className="h-4 w-4 text-muted-foreground/50 sm:h-5 sm:w-5" />
    </div>
  )
}

function FlowStageCard({ stage }: { stage: FlowStage }) {
  const Icon = stage.icon

  return (
    <div
      className={cn(
        'flex min-w-[148px] max-w-[180px] shrink-0 flex-col rounded-xl border p-3 shadow-sm sm:min-w-[160px]',
        stage.tone,
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-1">
        <span className={cn('flex h-7 w-7 items-center justify-center rounded-lg', stage.iconTone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="flex flex-col items-end gap-0.5">
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
            {stage.step}
          </Badge>
          {stage.highlight ? (
            <Badge className="h-4 px-1 text-[9px]">This page</Badge>
          ) : null}
        </div>
      </div>
      <p className="text-xs font-semibold leading-tight text-foreground">{stage.title}</p>
      <p className="mt-1 flex-1 text-[10px] leading-snug text-muted-foreground">{stage.summary}</p>
      <ul className="mt-2 space-y-0.5 border-t border-border/40 pt-2">
        {stage.records.map((record) => (
          <li key={record} className="font-mono text-[9px] leading-snug text-muted-foreground">
            · {record}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SettlementPaymentFlowDiagram() {
  return (
    <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
            Left → right
          </Badge>
          <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
            Layer 3 on this page
          </Badge>
        </div>
        <CardTitle className="text-base">Settlement money flow</CardTitle>
        <CardDescription>
          Customer and worker layers stay separate. Steps 4–5 (highlighted) are managed on this screen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pb-4">
        <div className="rounded-xl border border-dashed border-violet-200/80 bg-violet-50/50 px-4 py-3 text-sm dark:border-violet-900/40 dark:bg-violet-950/20">
          <p className="font-medium text-violet-950 dark:text-violet-100">How each job splits</p>
          <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-violet-900/90 dark:text-violet-100/90">
            <li>
              <strong>What the customer pays</strong> = what the job company earns + the booking company&apos;s commission
            </li>
            <li>
              <strong>What the job company earns</strong> = cash collected on the job + amount owed between companies
            </li>
          </ul>
        </div>

        <div className="-mx-1 overflow-x-auto px-1 pb-2">
          <div className="flex min-w-max items-stretch py-2">
            {FLOW_STAGES.map((stage, index) => (
              <div key={stage.id} className="flex items-stretch">
                <FlowStageCard stage={stage} />
                {index < FLOW_STAGES.length - 1 ? <StageArrow /> : null}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-900/30">
          <p className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Parallel track (not inter-company)
          </p>
          <div className="flex min-w-max items-stretch">
            {PARALLEL_STAGES.map((stage) => (
              <FlowStageCard key={stage.id} stage={stage} />
            ))}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Booking page', text: 'Customer payments', className: 'border-sky-200 bg-sky-50/80 text-sky-900' },
            { label: 'Job / trip', text: 'COD & ledger accrual', className: 'border-emerald-200 bg-emerald-50/80 text-emerald-900' },
            { label: 'This page', text: 'Invoices & payments', className: 'border-amber-200 bg-amber-50/80 text-amber-900' },
            { label: 'Payments to Company', text: 'Worker remittance', className: 'border-slate-200 bg-slate-50/80 text-slate-800' },
          ].map((item) => (
            <div key={item.label} className={cn('rounded-lg border p-3 text-xs', item.className)}>
              <p className="font-semibold">{item.label}</p>
              <p className="mt-0.5 opacity-80">{item.text}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
